// SAM chat card for listing the tenant's company processes (workflow types). First step in the
// "configure my company processes" flow — shows available workflows with stats; clicking
// "Customize" opens the WorkflowSettingsCard in the workspace without another SAM turn.
//
// Self-fetching: derives the workflow summary from useGetWorkflowCustomizationQuery (same RTK
// Query cache WorkflowSettingsCard uses), so no separate data prop is needed.
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/UI/Spinner';
import { useGetWorkflowCustomizationQuery } from '@/redux/internal-services/workflowEngineApi';
import { useListCatalogueEntriesQuery } from '@/redux/internal-services/platformAdminApi';

interface EngagementTypeOption { id: string; label: string }

export interface CompanyWorkflowsListCardProps {
  /** Called when the user clicks "Customize" — opens the WorkflowSettingsCard surface. */
  onCustomize: () => void;
  className?: string;
}

export const CompanyWorkflowsListCard: React.FC<CompanyWorkflowsListCardProps> = ({ onCustomize, className }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetWorkflowCustomizationQuery();
  const { data: catalogueData } = useListCatalogueEntriesQuery({ prefix: 'workflow_engine' });
  const engagementTypeLabels = useMemo(() => {
    const entry = catalogueData?.data.find((c) => c.key === 'workflow_engine');
    const list = (entry?.effective_value?.engagement_types as EngagementTypeOption[] | undefined) ?? [];
    return new Map(list.map((opt) => [opt.id, opt.label]));
  }, [catalogueData]);
  const etLabel = (et: string) =>
    engagementTypeLabels.get(et) ?? et.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (isLoading && !data) {
    return (
      <div className={cn('flex items-center justify-center gap-2 rounded-2xl border border-ui-border bg-ui-card p-8', className)}>
        <Spinner size="sm" /><span className="text-xs text-ui-text3">Loading company processes…</span>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className={cn('rounded-2xl border border-ui-border bg-ui-card p-4', className)}>
        <p className="text-xs text-[#CF4148]">{t('workflow.settings.companyProcessConfigUnavailable')}</p>
      </div>
    );
  }

  // Build the workflow summary from the full customization data.
  const summary = {
    name: data.name || 'Standard Workflow',
    description: data.description || '',
    engagement_types: data.engagement_types,
    stage_count: data.stages.length,
    process_count: data.processes.length,
    status_count: data.statuses.filter((s) => s.is_active).length,
    is_customized: data.source === 'tenant_fork',
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="rounded-xl border border-ui-border bg-ui-card p-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ui-text">{summary.name}</p>
            {summary.description && (
              <p className="mt-0.5 text-xs text-ui-text3">{summary.description}</p>
            )}
            {summary.engagement_types.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {summary.engagement_types.map((et) => (
                  <span
                    key={et}
                    className="rounded-full bg-brand-dark/10 px-2 py-0.5 text-[10px] font-medium text-brand-dark dark:bg-brand-dark/20"
                  >
                    {etLabel(et)}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-3">
              <span className="text-[10px] text-ui-text3">{summary.stage_count} stages</span>
              <span className="text-[10px] text-ui-text3">{summary.process_count} processes</span>
              <span className="text-[10px] text-ui-text3">{summary.status_count} active statuses</span>
              {summary.is_customized && (
                <span className="text-[10px] font-medium text-brand-dark">Customized</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onCustomize}
            className="shrink-0 rounded-lg bg-brand-dark px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-dark/90"
          >
            Customize
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyWorkflowsListCard;
